
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SlotGrid,
  cellKey,
  cellLabelForState,
  cellsToRanges,
  rangeDayCount,
  rangesToCells,
  useDayViewWindowSnap,
  useSlotCalendarGrid,
  type CellKey,
  type CellState,
  type InstantRange,
  type SlotCalendarRange,
  type SlotGridEvent,
} from '@client/shared/calendar/slot-grid';
import { Calendar } from '@client/shared/calendar/Calendar.tsx';
import {
  addDays as addDaysInstant,
  addWeeks,
  startOfDay,
  startOfWeek,
} from '@client/shared/calendar/week-grid.ts';
import { SLOT_MINUTES } from '@shared/booking';
import { dayHeaderLabel, formatSlotLabel } from '@client/shared/lib/wall-clock-intake.ts';
import { useBreakpoint } from '@client/shared/hooks/use-media-query.ts';
import { viewerTimeZone, viewerZoneLabel } from '@client/shared/lib/viewer-timezone.ts';
import { BlockConfirmPopover } from '@client/features/scheduling';
import { instantRangeToGridSpans, windowFor } from '@client/shared/calendar/booking-grid.ts';
import { editableSpan } from '@client/shared/calendar/editable-span.ts';
import { UnavailabilityBlock } from '@client/features/scheduling';
import { mergeReasonedRanges, type ReasonedRange } from './unavailability-ranges.ts';

export interface UnavailabilityCalendarProps {
  horizonDays: number;
  value: ReasonedRange[];
  onChange: (blocks: ReasonedRange[]) => void;
}

const VISIBLE_START_MIN = 8 * 60;
const VISIBLE_END_MIN = 20 * 60;
const VISIBLE_START_SLOT = VISIBLE_START_MIN / SLOT_MINUTES;
const VISIBLE_END_SLOT = VISIBLE_END_MIN / SLOT_MINUTES;

const NO_CELLS: ReadonlySet<CellKey> = new Set();

export function UnavailabilityCalendar({ horizonDays, value, onChange }: UnavailabilityCalendarProps) {
  const tz = viewerTimeZone();

  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [chosenRange, setChosenRange] = useState<SlotCalendarRange>('week');

  const breakpoint = useBreakpoint();
  const rangeForced = breakpoint !== 'desktop';
  const range: SlotCalendarRange =
    breakpoint === 'phone' ? 'day' : rangeForced ? '3day' : chosenRange;

  // ponytail: the create-form preview has no saved listing to read bookingWindowEnd from, so it
  // derives the window locally from the previewed horizon. Real listings use the server field.
  const windowEnd = useMemo(() => windowFor(new Date(), horizonDays).windowEnd, [horizonDays]);

  const { days, columns, rows, inWindow, canStepBack, canStepForward } = useSlotCalendarGrid({
    zone: tz,
    anchor,
    range,
    windowEnd,
    visibleStartMin: VISIBLE_START_MIN,
    visibleEndMin: VISIBLE_END_MIN,
  });

  const goToToday = useCallback(() => setAnchor(new Date()), []);

  useDayViewWindowSnap({ range, days, inWindow, onSnap: goToToday });

  const step = (delta: -1 | 1) => {
    setAnchor((prev) =>
      range === 'week'
        ? addWeeks(startOfWeek(prev, tz), tz, delta)
        : addDaysInstant(startOfDay(prev, tz), tz, delta * rangeDayCount(range)),
    );
  };

  const committedCells = useMemo(() => rangesToCells(value, days, tz), [value, days, tz]);

  const [painted, setPainted] = useState<ReadonlySet<CellKey>>(NO_CELLS);

  const [confirmAnchor, setConfirmAnchor] = useState<{ x: number; y: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const gridWrapRef = useRef<HTMLDivElement>(null);

  const daysKey = days.join('|');
  useEffect(() => {
    setPainted(NO_CELLS);
    setConfirmAnchor(null);
  }, [daysKey, committedCells]);

  const removeBlock = useCallback(
    (start: string) => onChange(value.filter((b) => b.start !== start)),
    [value, onChange],
  );

  const editBlockReason = useCallback(
    (start: string, reason: string) => {
      const trimmed = reason.trim();
      onChange(
        value.map((b) =>
          b.start === start
            ? trimmed === ''
              ? { start: b.start, end: b.end }
              : { ...b, reason: trimmed }
            : b,
        ),
      );
    },
    [value, onChange],
  );

  const blockEvents = useMemo<SlotGridEvent[]>(() => {
    const out: SlotGridEvent[] = [];
    for (const block of value) {
      const spans = instantRangeToGridSpans(new Date(block.start), new Date(block.end), days, tz, {
        startSlot: VISIBLE_START_SLOT,
        endSlot: VISIBLE_END_SLOT,
      });
      if (spans.length === 0) continue;
      spans.forEach((span) => {
        out.push({
          id: `unavail:${block.start}:${span.dayIndex}`,
          dayIndex: span.dayIndex,
          startSlot: span.startSlot,
          endSlot: span.endSlot,
          tone: 'blocked',
          interactive: true,
          content: (
            <UnavailabilityBlock
              reason={block.reason ?? null}
              createdAt={null}
              zone={tz}
              start={block.start}
              end={block.end}
              canEdit
              onEditReason={async (reason) => editBlockReason(block.start, reason)}
              onRemove={async () => removeBlock(block.start)}
            />
          ),
        });
      });
    }
    return out;
  }, [value, days, tz, editBlockReason, removeBlock]);

  const pendingAddedCells = useMemo(() => {
    const added = new Set<CellKey>();
    for (const key of painted) if (!committedCells.has(key)) added.add(key);
    return added;
  }, [painted, committedCells]);

  const pendingRanges = useMemo<InstantRange[]>(
    () => (pendingAddedCells.size === 0 ? [] : cellsToRanges(pendingAddedCells, days, tz)),
    [pendingAddedCells, days, tz],
  );

  const cellState = (day: number, slot: number): CellState => {
    if (!inWindow(days[day] ?? '')) return 'disabled';
    return committedCells.has(cellKey(day, slot)) ? 'unavailable' : 'free';
  };

  const span = useMemo(
    () =>
      editableSpan(pendingAddedCells, days, tz, cellState, {
        startSlot: VISIBLE_START_SLOT,
        endSlot: VISIBLE_END_SLOT,
      }),
    [pendingAddedCells, days, tz],
  );

  function onAdjust(edge: 'start' | 'end', delta: -1 | 1) {
    const nextPending = span?.nudge(edge, delta);
    if (!nextPending) return;
    setPainted(new Set<CellKey>(nextPending));
  }

  const cellLabel = (day: number, slot: number, state: CellState) =>
    cellLabelForState(
      state,
      dayHeaderLabel(days[day] ?? '', tz),
      formatSlotLabel(slot * SLOT_MINUTES),
      'blocked',
    );

  function onPaint(next: Set<CellKey>) {
    if (confirmAnchor) setConfirmAnchor(null);
    setPainted(next);
  }

  function onPaintEnd(point: { x: number; y: number } | null) {
    if (pendingAddedCells.size > 0) {
      setConfirmAnchor(point ?? { x: globalThis.innerWidth / 2, y: globalThis.innerHeight / 2 });
    }
  }

  function onConfirm(reason: string) {
    const trimmed = reason.trim();
    const visibleCells = new Set<CellKey>([...committedCells, ...painted]);
    onChange(mergeReasonedRanges(value, visibleCells, trimmed === '' ? undefined : trimmed, days, tz));
    setPainted(NO_CELLS);
    setConfirmAnchor(null);
  }

  function onCancel() {
    setPainted(NO_CELLS);
    setConfirmAnchor(null);
  }

  useEffect(() => {
    if (!confirmAnchor) return;
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || gridWrapRef.current?.contains(t)) return;
      setPainted(NO_CELLS);
      setConfirmAnchor(null);
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [confirmAnchor]);

  return (
    <>
      <Calendar
        anchor={anchor}
        zone={tz}
        range={range}
        onStep={step}
        onToday={goToToday}
        canStepBack={canStepBack}
        canStepForward={canStepForward}
        onRangeChange={rangeForced ? undefined : setChosenRange}
      >
        <div ref={gridWrapRef}>
          <SlotGrid
            ariaLabel="Unavailability calendar"
            columns={columns}
            rows={rows}
            painted={painted}
            onChange={onPaint}
            onPaintEnd={onPaintEnd}
            cellState={cellState}
            cellLabel={cellLabel}
            events={blockEvents}
            zoneLabel={viewerZoneLabel()}
          />
        </div>
      </Calendar>

      {confirmAnchor && pendingRanges.length > 0 && (
        <BlockConfirmPopover
          ref={popoverRef}
          ranges={pendingRanges}
          zone={tz}
          anchor={confirmAnchor}
          edit={span ? { times: span.times, bounds: span.bounds, onAdjust } : null}
          busy={false}
          error={false}
          onCommit={onConfirm}
          onCancel={onCancel}
        />
      )}
    </>
  );
}

export function cellsToBlocks(
  cells: Set<string>,
  days: string[],
  tz: string,
): InstantRange[] {
  return cellsToRanges(cells, days, tz);
}
